/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : states
 *   Feature      : 197-portal-light-theme-responsive (design blueprint)
 *   Description  : Canonical empty / error / loading states. One look for the
 *                  three states every data view needs, so panels stop
 *                  hand-rolling their own. Token-driven (light + dark).
 *   Blueprint    : docs/design-blueprint.md
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 * ============================================================================
 *
 *   🛑 THE `data-testid` ON EACH ROOT IS A PRODUCT CONTRACT, NOT TEST PLUMBING.
 *      Do not remove them, and do not reuse them elsewhere.
 *
 *      This is a customer-facing product: clients see the app and never the systems
 *      feeding it. The dangerous failure is therefore NOT an error — it is a screen
 *      that renders EMPTY, because "you have no backups" and "the query that finds
 *      your backups broke" look identical to the person reading it, and only one of
 *      them is true. That is exactly the failure mode an armed RLS FILTER produces:
 *      a predicate that matches nothing returns zero rows, not an exception.
 *
 *      `empty-state` / `error-state` / `loading-state` (and `not-collected` on the
 *      NotCollected component) let a test assert WHICH of those a page is in, so an
 *      emptied screen can be caught before a client reads it as fact. Without them
 *      the distinction exists in the design and is invisible to every automated check.
 */
import React from 'react';
import { clsx } from 'clsx';
import { Inbox, AlertTriangle } from 'lucide-react';

export interface EmptyStateProps {
  title?: React.ReactNode;
  message?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

/** Canonical "nothing here yet" state. */
export const EmptyState: React.FC<EmptyStateProps> = ({
  title = 'Nothing to show',
  message,
  icon,
  action,
  className,
}) => (
  <div
    data-testid="empty-state"
    className={clsx('flex flex-col items-center justify-center py-12 text-center', className)}
  >
    <div className="mb-3 text-[var(--color-text-secondary)] opacity-60">
      {icon ?? <Inbox className="h-10 w-10" />}
    </div>
    <p className="text-base font-medium text-[var(--color-text-primary)]">{title}</p>
    {message != null && <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{message}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export interface ErrorStateProps {
  title?: React.ReactNode;
  message?: React.ReactNode;
  /** May return a promise (a page's refresh); a rejection leaves this error state on screen. */
  onRetry?: () => void | Promise<unknown>;
  className?: string;
}

/** Canonical load-failure state with an optional retry. */
export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  message,
  onRetry,
  className,
}) => (
  <div
    data-testid="error-state"
    className={clsx(
      'rounded-lg border border-[var(--color-status-danger)]/40 bg-[var(--color-status-danger)]/5 p-4',
      className,
    )}
  >
    <div className="flex items-center gap-2 text-[var(--color-status-danger)]">
      <AlertTriangle className="h-5 w-5 flex-shrink-0" />
      <span className="font-medium">{title}</span>
    </div>
    {message != null && <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{message}</p>}
    {onRetry && (
      <button
        // A failed retry is already on screen: the page keeps rendering this state from its
        // query error. The rejected promise itself has no other reader, so it ends here.
        onClick={() => { void Promise.resolve(onRetry()).catch(() => undefined); }}
        className="mt-3 text-sm font-medium text-[var(--color-accent)] hover:underline"
      >
        Try again
      </button>
    )}
  </div>
);

export interface LoadingStateProps {
  /** Number of skeleton rows. */
  rows?: number;
  className?: string;
}

/** Canonical loading skeleton (use in place of bespoke animate-pulse blocks). */
export const LoadingState: React.FC<LoadingStateProps> = ({ rows = 4, className }) => (
  <div
    data-testid="loading-state"
    className={clsx('animate-pulse space-y-3', className)}
    aria-busy="true"
    aria-live="polite"
  >
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-10 rounded bg-[var(--color-bg-subtle)]" />
    ))}
  </div>
);
